import { signal } from "@preact/signals";
import type { EventPushResponse, VenueId } from "@orderman/shared";
import { pending, ack } from "./outbox.js";
import { kvGet, kvSet, outboxClear, ordersClear } from "./idb.js";
import { getDeviceToken, getDeviceLabel, getDeviceId, getResetGen, setResetGen, hasResetGen } from "./config.js";
import { currentUser } from "./auth.js";
import { pullMirror, getMirrorCursor, resetMirror } from "./mirror.js";

/**
 * Sync-Engine: schiebt die Outbox zum Server, sobald eine Verbindung besteht.
 * Robust gegen Offline-Phasen — fehlgeschlagene Sends bleiben in der Outbox.
 */

export const online = signal(navigator.onLine);
export const pendingCount = signal(0);
/** IDs der Bestellungen, von denen noch Events in der Outbox liegen (Status "ausstehend"). */
export const pendingOrderIds = signal<Set<string>>(new Set());
export const lastError = signal<string | null>(null);
/** Wurde zuletzt über die Cloud (statt direkt zum lokalen Server) gepusht? */
export const syncedViaCloud = signal(false);
/** Erreichbarkeit getrennt anzeigen: lokaler Server bzw. Cloud-Relay. */
export const localOnline = signal(false);
export const cloudOnline = signal(false);
/** Ist überhaupt ein Cloud-Fallback konfiguriert? */
export const cloudConfigured = signal(false);
/** Basis-URL des Cloud-Relays/-Admins (z. B. https://webon.plweb.at), sobald bekannt — für
 *  den remote „Verwaltung öffnen"-Link (Cloud-PHP-Admin der Veranstaltung). */
export const cloudBaseUrl = signal<string | null>(null);
/**
 * Wird die Küche gerade bedient? Im LAN-Betrieb trivial (wir erreichen den Server direkt).
 * Im Mobilfunk-Betrieb: synct der zugewiesene lokale Server noch mit der Cloud (frischer
 * Heartbeat)? Ist er still, werden gepushte Bons GERADE NICHT gedruckt → Warnbanner.
 * `null` = unbekannt (noch nicht ermittelt / Cloud-Status nicht abrufbar).
 */
export const kitchenOnline = signal<boolean | null>(null);
/**
 * Freischalt-Status dieses Geräts für den Cloud-Betrieb (nur im Mobilfunk relevant):
 * "unknown" = noch nicht geprüft · "ok" = zugewiesen & freigeschaltet (im_einsatz) ·
 * "not-registered" = kein Geräte-Token (nie enrollt / anderer Speicher) ·
 * "pending" = registriert + zugewiesen, aber vom Betreiber noch nicht freigeschaltet ·
 * "not-assigned" = keiner/anderer Veranstaltung zugewiesen (QR neu scannen) ·
 * "blocked" = gesperrt/ausgemustert.
 */
export const deviceActivation = signal<
  "unknown" | "ok" | "not-registered" | "pending" | "not-assigned" | "blocked"
>("unknown");

let getServerUrl: () => string = () => "";
let getVenueId: () => string = () => "";

/**
 * fetch mit hartem Timeout. Nötig für Anfragen an den lokalen Server: ist der gar nicht im
 * Netz (Kellner-Handy dauerhaft nur Mobilfunk), läuft ein blankes fetch sonst in den
 * OS-Default-Timeout (oft 60–120 s), bevor der Cloud-Fallback greift — die App wirkt
 * eingefroren (v. a. beim Venue-Wechsel, der auf flush() wartet). 3 s reichen im LAN locker.
 */
const LOCAL_TIMEOUT_MS = 3000;
function fetchLocal(url: string, init?: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), LOCAL_TIMEOUT_MS);
  // cache:"no-store": Liveness-/Status-Checks (/api/health, /api/cloud-fallback) dürfen NIE aus
  // dem Browser-HTTP-Cache kommen. Sonst bleibt z. B. eine früher (fälschlich) mit 200 gecachte
  // /api/health-Antwort „kleben" → lokaler Server gilt dauerhaft als online, obwohl er 404 gibt.
  return fetch(url, { cache: "no-store", ...init, signal: ctrl.signal }).finally(() => clearTimeout(t));
}

/**
 * fetch zur Cloud mit Timeout. Ohne Timeout kann ein Request bei wackeligem Internet (5G-Router,
 * halb-offene TCP-/H2-Verbindung) minutenlang hängen — im Push-Pfad blockiert das den
 * `flushing`-Riegel, und KEIN Bon geht mehr raus, auch nicht zum längst wieder erreichbaren
 * lokalen Server, bis die App neu startet (Erntedankfest 2026-09-27: 5 Bons 8–12 min hängend).
 */
const CLOUD_TIMEOUT_MS = 10_000;
function fetchCloud(url: string, init?: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), CLOUD_TIMEOUT_MS);
  return fetch(url, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(t));
}

/**
 * Cloud-Fallback-Konfig, vom lokalen Server (oder Voll-Cloud-Bootstrap) bezogen + gecacht:
 * `url` = Relay-Basis, `readKey` = read-only-Token (Sync-Status/Event-Pull; optional). Das
 * SCHREIB-Token ist NICHT hier, sondern pro Gerät (getDeviceToken(), beim Enroll bezogen) —
 * so kann nur ein freigeschaltetes Gerät über die Cloud bestellen.
 */
let cloudFallback: { url: string; readKey?: string } | null = null;

/** Schwelle, ab der ein Server-Heartbeat als "still" gilt (Küche offline). Großzügig gegen
 * einen verpassten Tick: Heartbeat alle ~4 s, Status-Poll alle 10 s. */
const KITCHEN_STALE_MS = 20_000;

/**
 * Cloud-Fallback-Konfig laden: zuerst Cache (für später, wenn der lokale Server weg ist),
 * dann – falls der lokale Server gerade erreichbar ist – frisch holen und cachen.
 */
export async function loadCloudFallback(): Promise<void> {
  const cached = await kvGet<{ url: string; readKey?: string }>("cloudFallback");
  if (cached) cloudFallback = { url: cached.url, ...(cached.readKey ? { readKey: cached.readKey } : {}) };
  try {
    const res = await fetchLocal(`${getServerUrl()}/api/cloud-fallback`);
    if (!res.ok) return;
    // Der lokale Server liefert evtl. noch ein `key`-Feld (alter push-only-Token) — ignoriert,
    // Schreiben läuft über den Geräte-Token. Nur url (+readKey) übernehmen.
    const cfg = (await res.json()) as { url: string | null; readKey?: string | null };
    if (cfg.url) {
      // Einen gültigen readKey NICHT durch eine Antwort ohne readKey verlieren (ältere
      // Provisionierung / Race): nur mit einem neuen, nicht-leeren Wert überschreiben.
      const readKey = cfg.readKey || cloudFallback?.readKey;
      cloudFallback = {
        url: cfg.url.replace(/\/+$/, ""),
        ...(readKey ? { readKey } : {}),
      };
      await kvSet("cloudFallback", cloudFallback);
    }
  } catch {
    /* lokaler Server nicht erreichbar – Cache gilt */
  } finally {
    cloudConfigured.value = cloudFallback !== null;
    cloudBaseUrl.value = cloudFallback?.url ?? null;
  }
}

/** Cloud-Lesezugang (Relay-URL + read-only-Token) für den Admin-Event-Pull im Mobilfunk. */
export function cloudRead(): { url: string; readKey?: string } | null {
  return cloudFallback ? { url: cloudFallback.url, ...(cloudFallback.readKey ? { readKey: cloudFallback.readKey } : {}) } : null;
}

/** Cloud-Fallback direkt setzen (Voll-Cloud-Modus: aus dem Bootstrap, ohne lokalen Server). */
export function setCloudFallback(url: string, readKey?: string): void {
  cloudFallback = { url: url.replace(/\/+$/, ""), ...(readKey ? { readKey } : {}) };
  cloudConfigured.value = true;
  cloudBaseUrl.value = cloudFallback.url;
  void kvSet("cloudFallback", cloudFallback);
}


interface SyncStatus {
  nowMs: number;
  serverLastSeenMs: number | null;
  configVersions: Record<string, number> | null;
  eventCursor: string;
  resetGen?: number;
}

/**
 * Live-Status der Venue aus der Cloud holen (sync-status.php, read-only-Token). Sagt uns, ob
 * der lokale Server noch mit der Cloud synct — die Voraussetzung dafür, dass gepushte Bons
 * überhaupt gedruckt/angezeigt werden. Gibt null zurück, wenn kein readKey vorliegt oder die
 * Cloud nicht erreichbar ist (dann bleibt der Küchen-Status "unbekannt", statt falsch "offline").
 */
async function pollSyncStatus(): Promise<SyncStatus | null> {
  if (!cloudFallback?.readKey || !navigator.onLine) return null;
  try {
    const res = await fetchCloud(
      `${cloudFallback.url}/sync-status.php?venue=${encodeURIComponent(getVenueId())}`,
      { headers: { "X-Api-Key": cloudFallback.readKey } },
    );
    if (!res.ok) return null;
    return (await res.json()) as SyncStatus;
  } catch {
    return null;
  }
}

/**
 * Freischaltung dieses Geräts prüfen (device-status.php): ist sein Geräte-Token einer
 * Veranstaltung zugewiesen und aktiv? So kann die PWA VOR dem ersten Bestellen warnen, statt
 * dass Cloud-Pushes später still mit 401 hängen. Nur im Mobilfunk-Betrieb relevant.
 */
async function checkActivation(): Promise<void> {
  const token = getDeviceToken();
  if (!token) {
    deviceActivation.value = "not-registered";
    return;
  }
  if (!cloudFallback || !navigator.onLine) return; // Status unverändert lassen
  try {
    const res = await fetchCloud(
      `${cloudFallback.url}/device-status.php?venue=${encodeURIComponent(getVenueId())}`,
      { headers: { "X-Device-Token": token } },
    );
    if (!res.ok) return;
    const d = (await res.json()) as { activated: boolean; reason: string };
    deviceActivation.value = d.activated
      ? "ok"
      : d.reason === "retired"
        ? "blocked"
        : d.reason === "pending"
          ? "pending"
          : "not-assigned";
  } catch {
    /* Cloud nicht erreichbar → Status unverändert */
  }
}

/**
 * Freischaltung im LAN prüfen: der lokale Server kennt die (aus der Cloud gesyncte) Sperrliste
 * und sagt per /api/device-status, ob DIESE deviceId gesperrt ist. So zeigt die PWA das
 * Sperr-Banner proaktiv — auch ohne Mobilfunk/Cloud, wo checkActivation() nicht läuft. Fehler
 * oder 404 (älterer lokaler Server) → Status unverändert, damit nie fälschlich „gesperrt".
 */
async function checkActivationLocal(): Promise<void> {
  try {
    const res = await fetchLocal(
      `${getServerUrl()}/api/device-status?deviceId=${encodeURIComponent(getDeviceId())}`,
    );
    if (!res.ok) return;
    const d = (await res.json()) as { activated: boolean; reason: string };
    // Der lokale Check kennt nur die Sperrliste: NUR nach "blocked" eskalieren bzw. eine bekannte
    // Sperre/Unbekanntheit nach "ok" auflösen. Ein vom Cloud-Check gesetztes "pending"/
    // "not-assigned" NICHT nach "ok" überschreiben (das weiß der lokale Server nicht).
    if (!d.activated) deviceActivation.value = "blocked";
    else if (deviceActivation.value === "blocked" || deviceActivation.value === "unknown")
      deviceActivation.value = "ok";
  } catch {
    /* lokaler Server nicht erreichbar → Status unverändert */
  }
}

/**
 * Präsenz melden: „dieses Handy lebt + dieser User ist eingeloggt" → Cloud (handy-seen.php),
 * damit der Admin sieht, wer auf welchem Handy online ist. Best-effort, device-token-authentifiziert
 * (das Handy markiert nur sich selbst). Läuft in beiden Modi (BYD erreicht die Cloud ohnehin;
 * Betreiber-Handys im LAN haben i. d. R. auch Internet). Fehler werden geschluckt.
 */
async function reportPresence(): Promise<void> {
  const token = getDeviceToken();
  if (!token || !navigator.onLine) return;
  const u = currentUser.value;
  // LAN: an den lokalen Server (er relayt zur Cloud, deckt Handys OHNE eigenes Internet ab);
  // Mobilfunk: direkt an die Cloud. Beide Wege → dasselbe handy-seen.php (per Geräte-Token).
  const base = localOnline.value ? getServerUrl() : cloudFallback?.url;
  if (!base) return;
  try {
    await fetchCloud(`${base}/handy-seen.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Device-Token": token },
      body: JSON.stringify({ userId: u?.id ?? null, userName: u?.name ?? null, deviceInfo: getDeviceLabel() }),
    });
  } catch {
    /* best-effort — Präsenz ist nicht kritisch */
  }
}

/**
 * Vom Server gemeldete Reset-Generation übernehmen: ist sie höher als die lokal bekannte, wurde
 * die Veranstaltung zurückgesetzt → Bestell-Cache leeren (Outbox verwirft stale Test-Bons,
 * „Meine Bons" + Cloud-Mirror weg), neue Generation merken, PWA neu laden. Karte/Personal liegen
 * serverseitig weiter vor und werden beim Neustart frisch geladen. Idempotent gegen Mehrfachlauf.
 */
let resetInProgress = false;
async function applyResetGen(gen: number): Promise<void> {
  const venue = getVenueId();
  if (resetInProgress || !Number.isFinite(gen)) return;
  // Server kennt eine NIEDRIGERE Generation (z. B. hatte das Handy vom früheren Server-Bug eine
  // fremde Venue-Generation gelernt): übernehmen, NICHT wipen — sonst erkennt es einen echten
  // späteren Reset (Server-gen steigt auf ≤ lokale) nie.
  if (gen < getResetGen(venue)) {
    setResetGen(venue, gen);
    return;
  }
  if (gen === getResetGen(venue)) return;
  // Erstkontakt mit der reset-Generation dieser Venue (lokal noch NIE gesetzt): das ist KEIN
  // echter Reset, sondern ein frisch zugewiesenes Gerät, das die aktuelle Generation zum ersten
  // Mal erfährt (z. B. weil das Seeden bei der Zuweisung nicht griff). Eine gerade erstellte
  // Bestellung ist NEU, nicht stale → nur die Generation übernehmen, NICHT wipen. Die offene
  // Bestellung bleibt in der Outbox und geht beim nächsten Flush (periodischer Retry, 8 s) unter
  // korrekter Generation raus. Nur wenn lokal SCHON eine Generation bekannt war, ist ein höherer
  // Server-Wert ein echter „Veranstaltung zurückgesetzt" → dann wie gehabt Cache leeren + Reload.
  if (!hasResetGen(venue)) {
    setResetGen(venue, gen);
    return;
  }
  resetInProgress = true;
  setResetGen(venue, gen);
  try {
    await outboxClear();
    await ordersClear();
    await resetMirror(venue as VenueId);
  } finally {
    location.reload();
  }
}

/** Aufeinanderfolgende fehlgeschlagene lokale Health-Checks. Ein einzelner Ausfall (WLAN-Blip,
 *  Band-Steering-Umschaltung) soll NICHT sofort auf "offline" kippen — sonst flackern Banner/
 *  Küchen-Status und der Push weicht unnötig auf die (fragile) Cloud aus. Erst nach mehreren
 *  Fehlversuchen in Folge gilt der lokale Server als weg; ein Erfolg stellt sofort wieder her. */
let localFailStreak = 0;
const LOCAL_OFFLINE_AFTER = 2; // Probe-Intervall 10 s → ~20 s Grace, bevor "lokal weg"

/** Erreichbarkeit von lokalem Server und Cloud-Relay prüfen (für die Status-Anzeige). */
export async function probeStatus(): Promise<void> {
  const wasLocalOnline = localOnline.value;
  let localOk = false;
  try {
    const r = await fetchLocal(`${getServerUrl()}/api/health`);
    localOk = r.ok;
  } catch {
    localOk = false;
  }
  if (localOk) {
    localFailStreak = 0;
    localOnline.value = true;
  } else {
    localFailStreak++;
    if (localFailStreak >= LOCAL_OFFLINE_AFTER) localOnline.value = false;
    // sonst: localOnline bleibt (Grace-Fenster gegen transiente Aussetzer)
  }
  // Lokaler Server gerade (wieder) erreichbar geworden → Cloud-Fallback-Key auffrischen.
  // Deckt Key-Rotation ab (Server re-enrollt/neu zugewiesen): ohne das bliebe ein gecachter,
  // inzwischen ungültiger Key aktiv, bis die App neu gestartet wird.
  if (localOnline.value && !wasLocalOnline) void loadCloudFallback();
  if (cloudFallback) {
    try {
      // Jede HTTP-Antwort (auch 400/401) heißt: Relay erreichbar. Nur Netzfehler = offline.
      await fetchCloud(`${cloudFallback.url}/events.php`, { method: "GET" });
      cloudOnline.value = true;
    } catch {
      cloudOnline.value = false;
    }
  } else {
    cloudOnline.value = false;
  }

  // Küchen-Status bestimmen.
  if (localOnline.value) {
    // LAN: wir erreichen den Server direkt → er bedient die Küche zweifelsfrei.
    kitchenOnline.value = true;
    // Freischaltung prüfen. Der lokale Server kennt nur die Sperrliste (im LAN pusht ein Handy
    // ohne Token, also DARF ein nur „verfuegbares" BYD-Handy hier senden). Die Cloud kennt den
    // vollen Status (im_einsatz vs. verfuegbar=pending). Mit Internet+Cloud+Token daher die
    // genauere Cloud-Prüfung, solange noch nicht "ok" — so lernt ein BYD-Handy sein "pending"
    // schon im WLAN (Banner erscheint dann ohne Verzögerung, sobald es auf Mobilfunk wechselt).
    if (deviceActivation.value !== "ok" && navigator.onLine && cloudFallback && getDeviceToken()) {
      void checkActivation();
    } else {
      void checkActivationLocal();
    }
  } else {
    // Mobilfunk: synct der zugewiesene lokale Server noch mit der Cloud? Alter des Heartbeats
    // gegen die SERVER-Uhr rechnen (Handy-Uhr kann schief gehen).
    const st = await pollSyncStatus();
    // Reset-Generation auch für die nur lesende Mobilfunk-PWA (die sonst nie pusht) übernehmen.
    if (st && typeof st.resetGen === "number") void applyResetGen(st.resetGen);
    if (st === null) {
      kitchenOnline.value = null; // unbekannt (kein readKey/Cloud nicht erreichbar) — nicht fälschlich "offline"
    } else {
      kitchenOnline.value =
        st.serverLastSeenMs !== null && st.nowMs - st.serverLastSeenMs <= KITCHEN_STALE_MS;
      // Cloud-Event-Mirror nachziehen, wenn der Cloud-Cursor vorgerückt ist (kein Blind-Poll).
      // Speist im Mobilfunk-Betrieb Bon-Status/-Nummer, verkaufte Mengen und Wartezeiten.
      if (cloudFallback?.readKey && st.eventCursor !== getMirrorCursor()) {
        void pullMirror(cloudFallback.url, cloudFallback.readKey, getVenueId() as VenueId);
      }
    }
    // Freischaltung prüfen, solange noch nicht bestätigt (danach nicht mehr — spart Anfragen).
    if (deviceActivation.value !== "ok") void checkActivation();
  }

  // Präsenz/Login melden (best-effort, ~10 s) — nach der Modus-Bestimmung, damit an den
  // richtigen Weg (lokaler Server bei LAN, sonst Cloud) geroutet wird.
  void reportPresence();
}

export async function refreshPendingCount(): Promise<void> {
  const items = await pending();
  pendingCount.value = items.length;
  // Nur Bestell-Events tragen eine orderId (Konfig-Snapshots ausfiltern).
  pendingOrderIds.value = new Set(
    items.flatMap((i) => (i.env.body.kind === "config.snapshot" ? [] : [i.env.body.orderId])),
  );
}

export function initSync(serverUrlGetter: () => string, venueIdGetter: () => string): void {
  getServerUrl = serverUrlGetter;
  getVenueId = venueIdGetter;
  window.addEventListener("online", () => {
    online.value = true;
    void loadCloudFallback();
    void probeStatus();
    void flush();
  });
  window.addEventListener("offline", () => {
    online.value = false;
    localOnline.value = false;
    cloudOnline.value = false;
    kitchenOnline.value = null; // ohne Netz unbekannt, ob die Küche bedient wird
  });
  window.setInterval(() => void flush(), 8000); // periodischer Retry
  window.setInterval(() => void probeStatus(), 10_000); // Erreichbarkeits-Status
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void probeStatus();
  });
  void loadCloudFallback().then(() => probeStatus());
  void refreshPendingCount();
}

let flushing = false;
let flushStartedMs = 0;
/** Notbremse: ein Flush, der länger läuft, gilt als hängend und blockiert neue nicht mehr.
 *  (Alle Requests haben Timeouts; das fängt nur Unvorhergesehenes ab. Doppeltes Senden ist
 *  unkritisch — Events sind per id idempotent.) */
const FLUSH_STALE_MS = 60_000;

/**
 * Versucht, alle ausstehenden Events zu senden. Idempotent gegen Mehrfachaufruf.
 *
 * `cloudVenueId` überschreibt die venueId im Cloud-Wrapper (nur für die Auth des Relays).
 * Default ist die aktuelle Venue; beim Venue-Wechsel wird die ALTE Venue übergeben, damit
 * deren Outbox mit dem (noch gecachten) alten Push-Key korrekt drainen kann statt 401.
 */
export async function flush(cloudVenueId?: string): Promise<void> {
  if (flushing && Date.now() - flushStartedMs < FLUSH_STALE_MS) {
    // Läuft schon — aber den Zähler auffrischen, damit ein neu eingereihter Bon sofort als
    // „↑ noch nicht übermittelt" sichtbar ist (sonst erst nach Ende des laufenden Flush).
    void refreshPendingCount();
    return;
  }
  flushing = true;
  flushStartedMs = Date.now();
  try {
    // Gesperrtes Gerät (proaktiv per probeStatus erkannt): gar nicht erst senden — die Events
    // bleiben in der Outbox, das Banner erklärt es. Wird zurückgesetzt, sobald der Betreiber die
    // Sperre aufhebt (nächster probeStatus meldet wieder "ok").
    if (deviceActivation.value === "blocked") {
      online.value = false;
      lastError.value = "Gerät gesperrt — Bestellungen können nicht gesendet werden.";
      return;
    }
    const items = await pending();
    if (items.length === 0) {
      online.value = navigator.onLine;
      return;
    }
    const events = items.map((i) => i.env);
    let acceptedIds: string[] | null = null;

    // 1. Direkt zum lokalen Server (Normalfall).
    let localBlocked = false;
    let localResetRequired = false;
    try {
      const res = await fetchLocal(`${getServerUrl()}/api/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events, resetGen: getResetGen(getVenueId()) }),
      });
      if (res.ok) {
        const data = (await res.json()) as EventPushResponse;
        acceptedIds = data.acceptedIds;
        syncedViaCloud.value = false;
        if (typeof data.resetGen === "number") void applyResetGen(data.resetGen);
      } else if (res.status === 403) {
        // Gerät ist ausgemustert → der lokale Server lehnt ab. NICHT über die Cloud weiter-
        // versuchen (dort ebenso gesperrt). Banner setzen, Events bleiben in der Outbox.
        deviceActivation.value = "blocked";
        localBlocked = true;
      } else if (res.status === 409) {
        // Venue wurde zurückgesetzt → veraltete Generation. Cache leeren + neu laden.
        const d = (await res.json().catch(() => ({}))) as { resetGen?: number };
        if (typeof d.resetGen === "number") void applyResetGen(d.resetGen);
        localResetRequired = true;
      }
    } catch {
      /* lokaler Server nicht erreichbar → Cloud-Fallback unten */
    }
    if (localBlocked) throw new Error("Gerät gesperrt — Bestellungen können nicht gesendet werden.");
    if (localResetRequired) {
      online.value = false;
      return; // PWA lädt gerade neu; alte Events sind nach dem Reset ohnehin weg.
    }

    // 2. Fallback: über die Cloud pushen, wenn lokal nicht ging, Internet da ist und konfiguriert.
    //    Auth per GERÄTE-Token (beim Enroll bezogen) — nur ein freigeschaltetes Gerät darf senden.
    if (acceptedIds === null && navigator.onLine && cloudFallback) {
      const deviceToken = getDeviceToken();
      if (!deviceToken) {
        throw new Error("Gerät nicht registriert — Bestellen über die Cloud erst nach Enroll (QR scannen).");
      }
      const res = await fetchCloud(`${cloudFallback.url}/events.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Device-Token": deviceToken },
        body: JSON.stringify({ venueId: cloudVenueId ?? getVenueId(), events, resetGen: getResetGen(getVenueId()) }),
      });
      if (res.status === 401) {
        // Genauen Grund holen (pending=noch nicht freigeschaltet vs. not-assigned vs. retired),
        // damit das Banner die richtige Aktion nennt statt pauschal „erneut scannen".
        void checkActivation();
        throw new Error(
          "Gerät nicht freigeschaltet — der Betreiber muss es im Admin aktivieren (oder den QR der Veranstaltung erneut scannen).",
        );
      }
      if (res.status === 409) {
        // Venue wurde zurückgesetzt → veraltete Generation. Cache leeren + neu laden.
        const d = (await res.json().catch(() => ({}))) as { resetGen?: number };
        if (typeof d.resetGen === "number") void applyResetGen(d.resetGen);
        online.value = false;
        return;
      }
      if (!res.ok) throw new Error(`Cloud antwortete ${res.status}`);
      acceptedIds = ((await res.json()) as EventPushResponse).acceptedIds;
      syncedViaCloud.value = true;
    }

    if (acceptedIds === null) throw new Error("Server nicht erreichbar");
    const accepted = new Set(acceptedIds);
    const doneSeqs = items.filter((i) => accepted.has(i.env.id)).map((i) => i.seq);
    await ack(doneSeqs);
    online.value = true;
    lastError.value = null;
  } catch (e) {
    online.value = false;
    lastError.value = e instanceof Error ? e.message : String(e);
  } finally {
    flushing = false;
    await refreshPendingCount();
  }
}
